import type { PaginatedResponse } from "@nuxt/app";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  AdminReferralAccount,
  AdminReferralAttribution,
  AdminReferralLedgerEntry,
  AdminReferralListParams,
  AdminReferralWithdrawalRequest,
  CreateAdminReferralAccountInput,
  CreateAdminReferralLedgerAdjustmentInput,
  CustomizeAdminReferralPartnershipInput,
  MarkReferralWithdrawalPaidInput,
  ReviewReferralWithdrawalInput,
} from "~/types/admin/referrals";

class AdminReferralsModule extends HttpFactory {
  async getAccounts(
    params: AdminReferralListParams = {},
  ): Promise<PaginatedResponse<AdminReferralAccount>> {
    return await this.get(ENDPOINTS.ADMIN.REFERRALS.ACCOUNTS, params);
  }

  async getAccount(accountId: number): Promise<AdminReferralAccount> {
    return await this.get(`${ENDPOINTS.ADMIN.REFERRALS.ACCOUNTS}${accountId}/`);
  }

  async createAccount(
    data: CreateAdminReferralAccountInput,
  ): Promise<AdminReferralAccount> {
    return await this.post(ENDPOINTS.ADMIN.REFERRALS.ACCOUNTS, data);
  }

  async activateAccount(accountId: number): Promise<AdminReferralAccount> {
    return await this.post(
      `${ENDPOINTS.ADMIN.REFERRALS.ACCOUNTS}${accountId}/activate/`,
      {},
    );
  }

  async pauseAccount(accountId: number): Promise<AdminReferralAccount> {
    return await this.post(
      `${ENDPOINTS.ADMIN.REFERRALS.ACCOUNTS}${accountId}/pause/`,
      {},
    );
  }

  async customizePartnership(
    accountId: number,
    data: CustomizeAdminReferralPartnershipInput,
  ): Promise<AdminReferralAccount> {
    return await this.post(
      `${ENDPOINTS.ADMIN.REFERRALS.ACCOUNTS}${accountId}/customize-partnership/`,
      data,
    );
  }

  async getAttributions(
    params: AdminReferralListParams = {},
  ): Promise<PaginatedResponse<AdminReferralAttribution>> {
    return await this.get(ENDPOINTS.ADMIN.REFERRALS.ATTRIBUTIONS, params);
  }

  async getLedger(
    params: AdminReferralListParams = {},
  ): Promise<PaginatedResponse<AdminReferralLedgerEntry>> {
    return await this.get(ENDPOINTS.ADMIN.REFERRALS.LEDGER, params);
  }

  async createLedgerAdjustment(
    data: CreateAdminReferralLedgerAdjustmentInput,
  ): Promise<AdminReferralLedgerEntry> {
    return await this.post(
      `${ENDPOINTS.ADMIN.REFERRALS.LEDGER}adjustments/`,
      data,
    );
  }

  async getWithdrawalRequests(
    params: AdminReferralListParams = {},
  ): Promise<PaginatedResponse<AdminReferralWithdrawalRequest>> {
    return await this.get(
      ENDPOINTS.ADMIN.REFERRALS.WITHDRAWAL_REQUESTS,
      params,
    );
  }

  async approveWithdrawal(
    withdrawalId: number,
    data: ReviewReferralWithdrawalInput = {},
  ): Promise<AdminReferralWithdrawalRequest> {
    return await this.post(
      `${ENDPOINTS.ADMIN.REFERRALS.WITHDRAWAL_REQUESTS}${withdrawalId}/approve/`,
      data,
    );
  }

  async rejectWithdrawal(
    withdrawalId: number,
    data: ReviewReferralWithdrawalInput = {},
  ): Promise<AdminReferralWithdrawalRequest> {
    return await this.post(
      `${ENDPOINTS.ADMIN.REFERRALS.WITHDRAWAL_REQUESTS}${withdrawalId}/reject/`,
      data,
    );
  }

  async markWithdrawalPaid(
    withdrawalId: number,
    data: MarkReferralWithdrawalPaidInput,
  ): Promise<AdminReferralWithdrawalRequest> {
    return await this.post(
      `${ENDPOINTS.ADMIN.REFERRALS.WITHDRAWAL_REQUESTS}${withdrawalId}/mark-paid/`,
      data,
    );
  }
}

export default AdminReferralsModule;
